'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ANALYTICS_CONSENT_KEY, updateAnalyticsConsent } from '@/lib/google-analytics';

/**
 * Pages captured as pixels into broadcasts and recordings, or shown on a TV: the banner would be
 * burned into the picture, and nobody there can answer it. The program route is the publisher's
 * capture page (`/townhall/<room>/program`), whatever the room type.
 */
export function isProgramSurfacePath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return pathname.startsWith('/program-surfaces/')
    || pathname === '/tv/direct'
    || pathname === '/tv/probe'
    || /^\/townhall\/[^/]+\/program\/?$/.test(pathname);
}

/**
 * CookieConsentBanner — A minimal bottom-bar consent banner for GA4 cookies.
 *
 * - On first visit (no stored preference): shows the banner.
 * - On Accept: updates gtag consent to 'granted', stores in localStorage.
 * - On Decline: stores 'denied' — GA4 runs in cookieless/consent-denied mode.
 * - On subsequent visits: applies stored preference silently, no banner shown.
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const pathname = usePathname();
  const isProgramSurface = isProgramSurfacePath(pathname);

  useEffect(() => {
    if (isProgramSurface) {
      setVisible(false);
      return;
    }

    try {
      const stored = localStorage.getItem(ANALYTICS_CONSENT_KEY);
      if (stored === 'granted' || stored === 'denied') {
        updateAnalyticsConsent(stored);
      } else {
        setVisible(true);
      }
    } catch {
      // localStorage unavailable (private browsing, etc.) — show banner
      setVisible(true);
    }
  }, [isProgramSurface]);

  function handleAccept() {
    updateAnalyticsConsent('granted');
    setVisible(false);
  }

  function handleDecline() {
    updateAnalyticsConsent('denied');
    setVisible(false);
  }

  if (!visible || isProgramSurface) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9000,
        backgroundColor: '#1a1a2e',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        flexWrap: 'wrap',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '14px',
        color: 'rgba(255,255,255,0.85)',
      }}
    >
      <span>
        We use cookies to analyze site usage and improve your experience.
        {' '}
        <a
          href="/privacy"
          style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', minHeight: '44px', padding: '0 4px' }}
        >
          Learn more
        </a>
      </span>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleAccept}
          style={{
            minHeight: '44px',
            padding: '6px 18px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: '#6366f1',
            color: '#fff',
            fontWeight: 600,
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          Accept
        </button>
        <button
          onClick={handleDecline}
          style={{
            minHeight: '44px',
            padding: '6px 18px',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.2)',
            backgroundColor: 'transparent',
            color: 'rgba(255,255,255,0.7)',
            fontWeight: 500,
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          Decline
        </button>
      </div>
    </div>
  );
}
